'use client';

import { Button } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { Close } from '@shared/icons';
import Link from 'next/link';
import { useState, type ReactNode } from 'react';
import { TextInput } from '../../form-controls/TextInput/TextInput';
import type { StaticLookupBlock, StaticLookupDataset } from '../types';

type StaticLookupProps = {
  block: StaticLookupBlock;
  dataset?: StaticLookupDataset;
  'data-testid'?: string;
};

type StaticLookupShellProps = {
  title: string;
  'data-testid': string;
  children: ReactNode;
};

type StaticLookupVariantProps = {
  block: StaticLookupBlock;
  dataTestId: string;
};

type StaticLookupInputProps = StaticLookupVariantProps & {
  dataset: StaticLookupDataset;
};

const debug = createDebug({ isEnabled: false });

/** Shared card wrapper: rounded container with the lookup title. */
function StaticLookupShell({ title, 'data-testid': dataTestId, children }: StaticLookupShellProps) {
  return (
    <div className="my-4 flex w-full flex-col gap-5 rounded-lg bg-container p-5" data-testid={dataTestId}>
      <h3 className="display-xs">{title}</h3>
      {children}
    </div>
  );
}

/** Fallback shown when the referenced dataset is missing. All copy comes from the block. */
function StaticLookupUnavailable({ block, dataTestId }: StaticLookupVariantProps) {
  return (
    <StaticLookupShell title={block.title} data-testid={`${dataTestId}-unavailable`}>
      <p className="paragraph text-foreground" role="status">
        {block.unavailableMessage}
      </p>
      {block.unavailableHref && block.unavailableLinkLabel ? (
        <Button asChild variant="secondary" size="s" className="self-start" data-testid={`${dataTestId}-unavailable-link`}>
          <Link href={block.unavailableHref}>{block.unavailableLinkLabel}</Link>
        </Button>
      ) : null}
    </StaticLookupShell>
  );
}

/** Numeric lookup: type a sum, read the matching value from the dataset. */
function StaticLookupInput({ block, dataset, dataTestId }: StaticLookupInputProps) {
  const [value, setValue] = useState('');
  const result = /^\d+$/.test(value) ? dataset.values[value] : undefined;
  const hasError = value !== '' && result === undefined;
  const inputId = `${dataTestId}-${block.source}-input`;
  const errorId = `${inputId}-error`;

  return (
    <StaticLookupShell title={block.title} data-testid={dataTestId}>
      <div className="flex w-full flex-col gap-2">
        <label htmlFor={inputId} className="paragraph-explanatory text-foreground whitespace-nowrap max-[20rem]:whitespace-normal" data-testid={`${dataTestId}-input-label`}>
          {block.inputLabel}
        </label>
        <div className="flex w-full items-center gap-4">
          <TextInput
            className="w-28 shrink-0"
            id={inputId}
            type="text"
            inputMode="numeric"
            min={dataset.min}
            max={dataset.max}
            maxLength={String(dataset.max).length}
            placeholder={block.placeholder}
            value={value}
            onChange={event => setValue(event.target.value)}
            helpMessageType={hasError ? 'error' : undefined}
            aria-describedby={hasError ? errorId : undefined}
            rightIcon={
              value ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => setValue('')}
                  aria-label={block.clearLabel}
                  data-testid={`${dataTestId}-clear`}
                >
                  <Close size="extra-small" />
                </Button>
              ) : undefined
            }
            data-testid={`${dataTestId}-input`}
          />
          <div className="flex h-12.5 min-w-0 flex-1 flex-col justify-center rounded-md bg-container-base px-4">
            <span className="paragraph-s text-foreground-subtle">{block.resultLabel}</span>
            <output htmlFor={inputId} className="display-xs" aria-live="polite" data-testid={`${dataTestId}-result`}>
              {result ?? '—'}
            </output>
          </div>
        </div>
        {hasError ? (
          <p id={errorId} className="paragraph-s-strong text-error-foreground" role="status" data-testid={`${dataTestId}-error`}>
            {block.validationMessage}
          </p>
        ) : null}
      </div>
    </StaticLookupShell>
  );
}

/**
 * Numeric lookup sourced from static page JSON.
 *
 * Renders {@link StaticLookupInput} when a dataset is provided. When it is
 * missing, logs the misconfiguration and renders {@link StaticLookupUnavailable}
 * so the page keeps working — its copy (message and optional PDF link) comes
 * entirely from the block, keeping the component reusable.
 *
 * @param block - Lookup config and fallback copy from page JSON
 * @param dataset - Route-owned min/max/values, or undefined when unavailable
 */
function StaticLookup({ block, dataset, 'data-testid': dataTestId = 'static-lookup' }: StaticLookupProps) {
  if (!dataset) {
    debug.forceError(`Missing static lookup dataset: ${block.source}`);

    return <StaticLookupUnavailable block={block} dataTestId={dataTestId} />;
  }

  return <StaticLookupInput block={block} dataset={dataset} dataTestId={dataTestId} />;
}

export { StaticLookup, type StaticLookupProps };
